import { AsyncBoundary } from "@components/AsyncBoundary";
import { PanelError } from "@scribenote/brian-design-system";
import type { PaneSelection } from "@subjects/paneSelection";
import type { SubjectIndex } from "@subjects/useSubjectIndex";
import { TaskCardItem } from "@tasks/TaskCardItem";
import { ViewByUuid, ViewReaderSkeleton } from "@views/ViewByUuid";
import { withSubjectFor } from "@views/viewIdentity";
import type { ReactNode } from "react";

/**
 * The subject pane's reading region (WEB-3288): whatever the rail has selected, at
 * full size, written on the open folder's manila. `onClearSelection` falls back to
 * the default, e.g. when the open view is deleted.
 */
export const SubjectReader = ({
  selection,
  index,
  subjectId,
  onClearSelection,
}: {
  selection: PaneSelection | null;
  index: SubjectIndex;
  /** The subject this pane is scoped to - see {@link withSubjectFor}. */
  subjectId: string;
  onClearSelection: () => void;
}) => {
  if (!selection)
    return (
      <ReaderRegion>
        <p className="p-7 text-sm text-annotation">
          Nothing here yet. I&rsquo;ll surface views and tasks as this visit
          moves.
        </p>
      </ReaderRegion>
    );

  if (selection.kind === "view") {
    // By-uuid fetch reports severity as calm (surfacing is per subject,
    // BE-1918); pass the rail's surfaced severity or the pane won't match its pill.
    const railView = index.views.find((view) => view.id === selection.id);
    const surfacedSeverity = index.surfaced.find(
      (view) => view.id === selection.id,
    )?.severity;
    return (
      <ReaderRegion>
        <ReaderContent
          label="this view"
          resetKey={selection.id}
          skeleton={<ViewReaderSkeleton />}
        >
          <ViewByUuid
            id={selection.id}
            severity={surfacedSeverity}
            // A client's or room's pane names the client or the room, not the
            // patient whose note this is (BE-2395).
            withSubject={railView ? withSubjectFor(railView, subjectId) : false}
            onDeleted={onClearSelection}
          />
        </ReaderContent>
      </ReaderRegion>
    );
  }

  const task = index.tasks.find((candidate) => candidate.id === selection.id);
  if (!task) return <ReaderRegion />;
  return (
    <ReaderRegion>
      <ReaderContent label="this to-do" resetKey={task.id}>
        <div className="min-h-0 flex-1 overflow-y-auto p-7">
          <TaskCardItem
            task={task}
            severity={task.severity}
            handleAction={index.handleAction}
          />
        </div>
      </ReaderContent>
    </ReaderRegion>
  );
};

/** The pane's right-hand column, which scrolls on its own. */
const ReaderRegion = ({ children }: { children?: ReactNode }) => (
  <div className="flex min-h-0 flex-1 flex-col" data-testid="subject-reader">
    {children}
  </div>
);

/**
 * The selected item's own boundary, so a bad a2ui tree costs a retry line here
 * rather than the whole pane, rail included. `resetKey` clears a stuck error when
 * the user picks something else.
 */
const ReaderContent = ({
  label,
  resetKey,
  skeleton = null,
  children,
}: {
  /** Noun for the error line, e.g. "this view". */
  label: string;
  resetKey: string;
  /** Only needed by children that suspend; a task renders from the index. */
  skeleton?: ReactNode;
  children: ReactNode;
}) => (
  <AsyncBoundary
    skeleton={skeleton}
    resetKeys={[resetKey]}
    fallback={(retry) => <PanelError label={label} onRetry={retry} />}
  >
    {children}
  </AsyncBoundary>
);
